import React from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { AlertCircle, Clock, Loader2, X } from 'lucide-react';
import PmoModalActions from './PmoModalActions';

/** confirm → converting → (if the request runs long) slow. */
export type ConvertToScrumStage = 'confirm' | 'converting' | 'slow';

interface PmoConvertToScrumModalProps {
  isOpen: boolean;
  stage: ConvertToScrumStage;
  projectName: string;
  openItemCount: number;
  doneItemCount: number;
  onClose: () => void;
  onConfirm: () => void;
}

const pluralize = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * Confirms a Kanban → Scrum conversion, then stays open while it runs. Can't
 * be dismissed mid-run; once the run passes the slow mark it can, and the
 * conversion carries on server-side.
 */
const PmoConvertToScrumModal: React.FC<PmoConvertToScrumModalProps> = ({
  isOpen,
  stage,
  projectName,
  openItemCount,
  doneItemCount,
  onClose,
  onConfirm,
}) => {
  const isConverting = stage === 'converting';
  const close = () => {
    if (!isConverting) onClose();
  };

  const title = {
    confirm: `Convert ${projectName} to Scrum?`,
    converting: 'Converting to Scrum',
    slow: 'This is taking longer than usual',
  }[stage];

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={close}>
        <TransitionChild
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="flex w-full max-w-[440px] flex-col gap-4 rounded-2xl border border-primary-intemediate bg-primary-light p-5 text-left shadow-2xl">
                <div className="flex items-center justify-between gap-4">
                  <DialogTitle as="h3" className="text-base font-semibold text-white">
                    {title}
                  </DialogTitle>
                  {!isConverting && (
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={close}
                      className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-neutral transition-colors hover:text-white focus:outline-none"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {stage === 'confirm' && (
                  <>
                    <div className="flex flex-col gap-1.5 text-sm text-neutral">
                      <p>
                        {pluralize(openItemCount, 'open work item')} will be moved to the Scrum
                        Backlog.
                      </p>
                      <p>
                        {pluralize(doneItemCount, 'completed item')} will remain unchanged.
                      </p>
                      <p>Your team will now organize and manage work through Sprints.</p>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-xs text-red-400">
                      <AlertCircle size={14} className="flex-shrink-0" />
                      This action cannot be undone
                    </div>
                    <div className="flex justify-end gap-3">
                      <PmoModalActions
                        onCancel={close}
                        onConfirm={onConfirm}
                        confirmText="Convert to Scrum"
                      />
                    </div>
                  </>
                )}

                {isConverting && (
                  <>
                    <div className="flex flex-col items-center gap-2 py-2 text-center">
                      <Loader2 size={24} className="mb-1 animate-spin text-[#8B5CF6]" />
                      <p className="text-sm font-medium text-white">
                        Converting your project, this may take a minute
                      </p>
                      <p className="text-xs text-neutral-medium">
                        Keep this page open. Large projects can take longer.
                      </p>
                    </div>
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        disabled
                        className="rounded-lg border border-primary-subtle bg-primary-variant px-4 py-2 text-sm text-neutral opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled
                        className="rounded-lg border border-primary-subtle bg-primary-variant px-4 py-2 text-sm text-neutral opacity-50"
                      >
                        Converting…
                      </button>
                    </div>
                  </>
                )}

                {stage === 'slow' && (
                  <>
                    <div className="flex flex-col items-center gap-2 py-2 text-center">
                      <Clock size={24} className="mb-1 text-[#F59E0B]" />
                      <p className="text-sm font-medium text-white">
                        Your project is still converting in the background.
                      </p>
                      <p className="text-xs text-neutral-medium">
                        We'll notify you when it's done. The project stays in Kanban until then.
                      </p>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={close}
                        className="button-active rounded-lg px-4 py-2 text-sm font-medium text-white"
                      >
                        Close
                      </button>
                    </div>
                  </>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PmoConvertToScrumModal;
